import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

const CHAR_DELAY = 22;

export default function CTFSection({ ctfData }) {
    const termRef = useRef(null);
    const inputRef = useRef(null);
    const bodyRef = useRef(null);

    const categories = ctfData?.categories || [];
    const prizes = ctfData?.prizes || [];
    const TREE = ctfData?.interactiveTree || {};

    const [started, setStarted] = useState(false);
    const [nodeId, setNodeId] = useState('start');
    const [history, setHistory] = useState([]);
    const [typingText, setTypingText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [inputActive, setInputActive] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [inputError, setInputError] = useState('');
    const timers = useRef([]);

    const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

    const scrollBottom = () => {
        setTimeout(() => {
            if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }, 50);
    };

    const typeText = useCallback((text, onDone) => {
        clearTimers();
        setIsTyping(true);
        setTypingText('');
        setInputActive(false);
        for (let i = 0; i <= text.length; i++) {
            const t = setTimeout(() => {
                setTypingText(text.slice(0, i));
                scrollBottom();
                if (i === text.length) {
                    setIsTyping(false);
                    setInputActive(true);
                    setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
                    if (onDone) onDone();
                }
            }, i * CHAR_DELAY);
            timers.current.push(t);
        }
    }, []);

    const goToNode = useCallback((id) => {
        const node = TREE[id];
        if (!node) return;
        setNodeId(id);
        setInputError('');
        if (node.answer) {
            typeText(node.text);
        } else {
            typeText(node.question);
        }
        scrollBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeText]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !started) {
                    setStarted(true);
                    observer.disconnect();
                    setTimeout(() => goToNode('start'), 400);
                }
            },
            { threshold: 0.15 }
        );
        if (termRef.current) observer.observe(termRef.current);
        return () => { observer.disconnect(); clearTimers(); };
    }, [started, goToNode]);

    const handleKeyDown = (e) => {
        if (e.key !== 'Enter' || !inputActive || isTyping) return;
        const val = userInput.trim();
        const node = TREE[nodeId];

        if (node.answer) {
            const entry = { type: 'answer-nav', userInput: val || 'back', nextId: node.back };
            setHistory(prev => [...prev, { nodeId, node, entry }]);
            setUserInput('');
            setTypingText('');
            setInputActive(false);
            setTimeout(() => goToNode(node.back), 200);
            scrollBottom();
            return;
        }

        const option = node.options.find(o => o.key === val);
        if (!option) {
            setInputError(`Invalid option "${val}". Enter a number from the list.`);
            setUserInput('');
            return;
        }

        setInputError('');
        const entry = { type: 'choice', userInput: val, label: option.label, nextId: option.next };
        setHistory(prev => [...prev, { nodeId, node, entry }]);
        setUserInput('');
        setTypingText('');
        setInputActive(false);
        setTimeout(() => goToNode(option.next), 200);
        scrollBottom();
    };

    const currentNode = TREE[nodeId];

    if (!ctfData) return null;

    return (
        <section id="ctf" className="bsides-ctf" ref={termRef}>
            <style>{`
                .bsides-ctf {
                    background: linear-gradient(180deg, var(--eventflow-black) 0%, #0a0014 50%, var(--eventflow-black) 100%);
                    padding: 100px 0;
                    position: relative;
                    overflow: hidden;
                }
                .bsides-ctf::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: radial-gradient(ellipse at 50% 0%, rgba(var(--eventflow-primary-rgb),0.15) 0%, transparent 60%);
                    pointer-events: none;
                }

                /* Interactive Terminal */
                .ctf-iterminal {
                    background: #0d0d0d;
                    border: 1px solid rgba(var(--eventflow-base-rgb), 0.25);
                    border-radius: 12px;
                    overflow: hidden;
                    margin-bottom: 60px;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(var(--eventflow-primary-rgb),0.08);
                    max-width: 860px;
                    margin-left: auto;
                    margin-right: auto;
                }
                .ctf-iterminal__bar {
                    background: #1a1a1a;
                    padding: 10px 16px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(255,255,255,0.06);
                }
                .ctf-iterminal__dots { display: flex; gap: 7px; }
                .ctf-iterminal__dot { width: 12px; height: 12px; border-radius: 50%; }
                .ctf-iterminal__dot--red   { background: #ff5f57; }
                .ctf-iterminal__dot--yellow { background: #febc2e; }
                .ctf-iterminal__dot--green  { background: #28c840; }
                .ctf-iterminal__title {
                    font-family: 'Courier New', monospace;
                    font-size: 12px;
                    color: rgba(255,255,255,0.4);
                }
                .ctf-iterminal__hint {
                    font-family: 'Courier New', monospace;
                    font-size: 11px;
                    color: rgba(0,255,65,0.3);
                }
                .ctf-iterminal__body {
                    padding: 24px 28px;
                    font-family: 'Courier New', monospace;
                    font-size: 14px;
                    line-height: 1.85;
                    min-height: 420px;
                    max-height: 480px;
                    overflow-y: auto;
                    background: #000;
                    position: relative;
                    scroll-behavior: smooth;
                }
                .ctf-iterminal__body::-webkit-scrollbar { width: 4px; }
                .ctf-iterminal__body::-webkit-scrollbar-track { background: transparent; }
                .ctf-iterminal__body::-webkit-scrollbar-thumb { background: rgba(0,255,65,0.15); border-radius: 2px; }
                .ctf-iterminal__body::after {
                    content: ''; position: absolute; inset: 0;
                    background: repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,0,0,0.05) 3px, rgba(0,0,0,0.05) 4px);
                    pointer-events: none; z-index: 20;
                }

                /* Terminal text styles */
                .ctf-it-block { margin-bottom: 16px; }
                .ctf-it-divider { border: none; border-top: 1px solid rgba(0,255,65,0.07); margin: 12px 0; }
                .ctf-it-q { color: rgba(0,255,65,0.6); font-style: italic; white-space: pre-line; margin-bottom: 6px; }
                .ctf-it-opts { margin-bottom: 6px; }
                .ctf-it-opt { color: rgba(0,255,65,0.5); padding-left: 4px; }
                .ctf-it-opt-key { color: #00ff41; font-weight: 700; text-shadow: 0 0 8px #00ff41; }
                .ctf-it-choice {
                    display: flex; align-items: center; gap: 8px; margin-bottom: 4px;
                    color: #00ff41; text-shadow: 0 0 6px rgba(0,255,65,0.5);
                }
                .ctf-it-choice-arrow { color: #00e676; font-weight: 700; }
                .ctf-it-typing { color: rgba(0,255,65,0.6); font-style: italic; white-space: pre-line; }
                .ctf-it-cursor {
                    display: inline-block; width: 7px; height: 14px;
                    background: rgba(0,255,65,0.5);
                    animation: ctfItBlink 0.8s step-end infinite; vertical-align: middle; margin-left: 2px;
                }
                @keyframes ctfItBlink { 0%,100%{opacity:1} 50%{opacity:0} }
                .ctf-it-back-hint { color: rgba(0,255,65,0.3); font-size: 12px; font-style: italic; margin-top: 4px; }
                .ctf-it-input-line { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
                .ctf-it-ps { color: #00ff41; font-weight: 700; text-shadow: 0 0 10px #00ff41; flex-shrink: 0; }
                .ctf-it-input {
                    background: transparent; border: none; outline: none;
                    font-family: 'Courier New', monospace; font-size: 14px;
                    color: #00ff41; text-shadow: 0 0 8px rgba(0,255,65,0.6);
                    caret-color: #00ff41; flex: 1; min-width: 0;
                }
                .ctf-it-input::placeholder { color: rgba(0,255,65,0.2); font-style: italic; }
                .ctf-it-error { color: #ff4444; font-size: 12px; margin-top: 4px; padding-left: 4px; }

                /* categories */
                .ctf-categories {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    margin-bottom: 60px;
                }
                .ctf-cat {
                    background: rgba(var(--eventflow-white-rgb), 0.03);
                    border: 1px solid rgba(var(--eventflow-white-rgb), 0.07);
                    border-radius: 12px;
                    padding: 24px 20px;
                    transition: all 0.3s;
                    position: relative;
                    overflow: hidden;
                }
                .ctf-cat::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0; right: 0;
                    height: 2px;
                    background: linear-gradient(90deg, var(--eventflow-base), var(--eventflow-primary));
                    opacity: 0;
                    transition: opacity 0.3s;
                }
                .ctf-cat:hover { border-color: rgba(var(--eventflow-base-rgb),0.3); transform: translateY(-4px); }
                .ctf-cat:hover::before { opacity: 1; }
                .ctf-cat__header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 12px;
                }
                .ctf-cat__icon {
                    width: 40px; height: 40px;
                    border-radius: 8px;
                    background: rgba(var(--eventflow-base-rgb),0.12);
                    display: flex; align-items: center; justify-content: center;
                }
                .ctf-cat__icon i { font-size: 16px; color: var(--eventflow-base); }
                .ctf-cat__pts {
                    font-family: 'Courier New', monospace;
                    font-size: 11px;
                    color: var(--eventflow-base);
                    background: rgba(var(--eventflow-base-rgb),0.1);
                    border: 1px solid rgba(var(--eventflow-base-rgb),0.2);
                    border-radius: 20px;
                    padding: 3px 10px;
                }
                .ctf-cat__title {
                    font-family: var(--eventflow-font);
                    font-size: 15px;
                    font-weight: 700;
                    color: var(--eventflow-white);
                    margin-bottom: 8px;
                }
                .ctf-cat__desc {
                    font-family: var(--eventflow-font);
                    font-size: 13px;
                    line-height: 1.6;
                    color: rgba(var(--eventflow-white-rgb),0.45);
                    margin: 0;
                }

                /* prizes */
                .ctf-prizes {
                    display: flex;
                    gap: 20px;
                    justify-content: center;
                    flex-wrap: wrap;
                    margin-bottom: 48px;
                }
                .ctf-prize {
                    flex: 1;
                    min-width: 220px;
                    max-width: 300px;
                    background: rgba(var(--eventflow-white-rgb),0.03);
                    border: 1px solid rgba(var(--eventflow-white-rgb),0.08);
                    border-radius: 16px;
                    padding: 32px 24px;
                    text-align: center;
                    transition: transform 0.3s;
                }
                .ctf-prize:hover { transform: translateY(-4px); }
                .ctf-prize__icon {
                    font-size: 40px;
                    margin-bottom: 12px;
                    display: block;
                }
                .ctf-prize__rank {
                    font-family: var(--eventflow-font);
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    color: rgba(var(--eventflow-white-rgb),0.4);
                    margin-bottom: 8px;
                }
                .ctf-prize__value {
                    font-family: var(--eventflow-font);
                    font-size: 15px;
                    font-weight: 700;
                    color: var(--eventflow-white);
                }

                /* cta */
                .ctf-cta {
                    text-align: center;
                    padding: 48px 40px;
                    background: rgba(var(--eventflow-primary-rgb),0.06);
                    border: 1px solid rgba(var(--eventflow-primary-rgb),0.15);
                    border-radius: 16px;
                }
                .ctf-cta h3 {
                    font-family: var(--eventflow-font);
                    font-size: 30px;
                    font-weight: 800;
                    color: var(--eventflow-white);
                    margin-bottom: 12px;
                }
                .ctf-cta p {
                    font-size: 15px;
                    color: rgba(var(--eventflow-white-rgb),0.55);
                    margin-bottom: 28px;
                    max-width: 500px;
                    margin-left: auto;
                    margin-right: auto;
                }
                .ctf-cta__btns {
                    display: flex;
                    gap: 16px;
                    justify-content: center;
                    flex-wrap: wrap;
                }

                @media (max-width: 991px) { .ctf-categories { grid-template-columns: repeat(2,1fr); } }
                @media (max-width: 576px) {
                    .ctf-categories { grid-template-columns: 1fr; }
                    .ctf-iterminal__body { padding: 16px 14px; font-size: 11.5px; min-height: 340px; max-height: 400px; }
                    .bsides-section-title { font-size: 36px; }
                    .ctf-iterminal__title, .ctf-iterminal__hint { display: none; }
                }
            `}</style>

            <div className="container">
                {/* Header */}
                <div className="text-center mb-5 wow fadeInUp" data-wow-delay="100ms">
                    <div className="bsides-section-tag" style={{justifyContent:'center'}}>{ctfData.section.eyebrow}</div>
                    <h2 className="bsides-section-title">{ctfData.section.title} <span>{ctfData.section.titleAccent}</span> {ctfData.section.titleEnd}</h2>
                    <p style={{color:'rgba(255,255,255,0.55)', maxWidth:'560px', margin:'0 auto', fontSize:'16px', lineHeight:'1.75'}}>
                        {ctfData.section.subtitle}
                    </p>
                </div>

                {/* Interactive Terminal */}
                <div className="ctf-iterminal wow fadeInUp" data-wow-delay="150ms">
                    <div className="ctf-iterminal__bar">
                        <div className="ctf-iterminal__dots">
                            <span className="ctf-iterminal__dot ctf-iterminal__dot--red" />
                            <span className="ctf-iterminal__dot ctf-iterminal__dot--yellow" />
                            <span className="ctf-iterminal__dot ctf-iterminal__dot--green" />
                        </div>
                        <span className="ctf-iterminal__title">{ctfData.terminal.barTitle}</span>
                        <span className="ctf-iterminal__hint">{ctfData.terminal.barHint}</span>
                    </div>
                    <div className="ctf-iterminal__body" ref={bodyRef} onClick={() => inputRef.current && inputRef.current.focus()}>

                        {/* History */}
                        {history.map((item, i) => (
                            <div className="ctf-it-block" key={i}>
                                {i > 0 && <hr className="ctf-it-divider" />}
                                <div className="ctf-it-q">{item.node.answer ? item.node.text : item.node.question}</div>
                                {!item.node.answer && item.node.options && (
                                    <div className="ctf-it-opts">
                                        {item.node.options.map(o => (
                                            <div className="ctf-it-opt" key={o.key}>
                                                <span className="ctf-it-opt-key">[{o.key}]</span> {o.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="ctf-it-choice">
                                    <span className="ctf-it-choice-arrow">›</span>
                                    <span>{item.entry.userInput}{item.entry.label ? ` — ${item.entry.label}` : ''}</span>
                                </div>
                            </div>
                        ))}

                        {/* Current node */}
                        {typingText !== '' && (
                            <div className="ctf-it-block">
                                {history.length > 0 && <hr className="ctf-it-divider" />}

                                <div className="ctf-it-typing">
                                    {typingText}
                                    {isTyping && <span className="ctf-it-cursor" />}
                                </div>

                                {!isTyping && currentNode && !currentNode.answer && (
                                    <div className="ctf-it-opts" style={{marginTop:'6px'}}>
                                        {currentNode.options.map(o => (
                                            <div className="ctf-it-opt" key={o.key}>
                                                <span className="ctf-it-opt-key">[{o.key}]</span> {o.label}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!isTyping && currentNode && currentNode.answer && (
                                    <div className="ctf-it-back-hint">Press Enter to go back</div>
                                )}

                                {inputActive && (
                                    <>
                                        <div className="ctf-it-input-line">
                                            <span className="ctf-it-ps">$</span>
                                            <input
                                                ref={inputRef}
                                                className="ctf-it-input"
                                                type="text"
                                                value={userInput}
                                                onChange={e => { setUserInput(e.target.value); setInputError(''); }}
                                                onKeyDown={handleKeyDown}
                                                placeholder={currentNode && currentNode.answer ? 'press Enter...' : 'enter option number...'}
                                                autoComplete="off"
                                                spellCheck="false"
                                                maxLength={2}
                                            />
                                        </div>
                                        {inputError && <div className="ctf-it-error">✗ {inputError}</div>}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Categories */}
                <div className="ctf-categories wow fadeInUp" data-wow-delay="200ms">
                    {categories.map((c, i) => (
                        <div className="ctf-cat" key={i}>
                            <div className="ctf-cat__header">
                                <div className="ctf-cat__icon"><i className={`fa ${c.icon}`} /></div>
                                <span className="ctf-cat__pts">{c.points}</span>
                            </div>
                            <div className="ctf-cat__title">{c.title}</div>
                            <p className="ctf-cat__desc">{c.desc}</p>
                        </div>
                    ))}
                </div>

                {/* Prizes */}
                <div className="text-center mb-4 wow fadeInUp" data-wow-delay="100ms">
                    <div className="bsides-section-tag" style={{justifyContent:'center'}}>{ctfData.section.prizesEyebrow}</div>
                    <h3 style={{fontFamily:'var(--eventflow-font)',fontSize:'32px',fontWeight:'800',color:'var(--eventflow-white)',marginBottom:'32px'}}>{ctfData.section.prizesTitle}</h3>
                </div>
                <div className="ctf-prizes wow fadeInUp" data-wow-delay="200ms">
                    {prizes.map((p, i) => (
                        <div className="ctf-prize" key={i} style={{borderColor: `${p.color}22`}}>
                            <span className="ctf-prize__icon" style={{color: p.color}}><i className={`fa ${p.icon}`} /></span>
                            <div className="ctf-prize__rank">{p.rank} Place</div>
                            <div className="ctf-prize__value">{p.prize}</div>
                        </div>
                    ))}
                </div>

                {/* CTA */}
                <div className="ctf-cta wow fadeInUp" data-wow-delay="300ms">
                    <h3>{ctfData.section.ctaTitle}</h3>
                    <p>{ctfData.section.ctaSubtitle}</p>
                    <div className="ctf-cta__btns">
                        <Link to={ctfData.section.ctaButtonLink} className="thm-btn">{ctfData.section.ctaButtonText} <span className="icon-arrow-right" /></Link>
                        <Link to={ctfData.section.ctaButton2Link} className="thm-btn-outline">{ctfData.section.ctaButton2Text} <span className="icon-arrow-right" /></Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
